import { Form } from "@circle-react-uikit/Form";
import { useSpaceGroupsContext } from "@circle-react/contexts/spaceGroupsContext";
import { isChatSpace } from "@circle-react/helpers/spaceHelpers";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { Typography } from "@circleco/compass/components/Typography";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { Badge } from "@/react/components/SettingsApp/CompassCompat/Badge";

import type { SpaceFormValues } from "../../types";

interface AutoAddMembersFieldProps {
  chatSpaceMemberLimit: number;
  spaceGroupMemberCount: number;
}

export const AutoAddMembersField = ({
  chatSpaceMemberLimit,
  spaceGroupMemberCount,
}: AutoAddMembersFieldProps) => {
  const {
    helpers: { findBy },
  } = useSpaceGroupsContext();
  const { setValue } = useFormContext<SpaceFormValues>();

  const spaceGroupId = useWatch<SpaceFormValues, "space_group_id">({
    name: "space_group_id",
  });
  const postType = useWatch<SpaceFormValues, "post_type">({
    name: "post_type",
  });
  const spaceGroup = findBy({ id: spaceGroupId });

  useEffect(() => {
    if (spaceGroup) {
      setValue(
        "add_members_from_space_group",
        spaceGroup.automatically_add_members_to_new_spaces,
      );
    }
  }, [spaceGroupId, setValue, spaceGroup]);

  const isToggleDisabled =
    isChatSpace({ post_type: postType }) &&
    spaceGroupMemberCount > chatSpaceMemberLimit;

  const label = (
    <div className="flex items-center gap-1">
      <Typography variant="label-sm">
        {t("settings.access_groups.admin.legacy_access.title")}
      </Typography>
      <Tooltip
        content={t("settings.access_groups.admin.legacy_access.legacy_tooltip")}
      >
        <Badge
          square
          label={t("settings.access_groups.admin.legacy_access.legacy_badge")}
        />
      </Tooltip>
    </div>
  );

  const formItem = (
    <Form.Item
      name="add_members_from_space_group"
      label={label}
      description={t("spaces.form.add_members_from_space_this_group")}
      hideBorder
      inline
      shouldToggleValueOnLabelClick
      isDisabled={isToggleDisabled}
    >
      <Form.ToggleSwitch
        name="add_members_from_space_group"
        variant="small"
        disabled={isToggleDisabled}
      />
    </Form.Item>
  );

  if (isToggleDisabled) {
    return (
      <Tooltip content={t("members_in_space_group_limit_reached")}>
        {formItem}
      </Tooltip>
    );
  }

  return formItem;
};
